/**
 * Helpers para mostrar tiempos por status del pedido en los paneles
 * admin. Expone dos funciones:
 *
 *  - `computeStageAges(order)` → ordena el `status_history` y devuelve
 *    la edad de cada transición ("hace N min/h") en tiempo real.
 *    Retorna también un string compacto para mostrar inline.
 *
 *  - `totalElapsedMs(order)` → ms totales entre `created_at` y el
 *    cierre (`delivered`/`cancelled`) o `now` si sigue abierto.
 *
 * Los colores y formatos siguen la misma lógica que `Elapsed` (gris
 * neutral <15m, ámbar 15-30m, rojo >30m) para que el UI sea consistente.
 */

const STATUS_LABEL = {
  pending: "Recibido",
  confirmed: "Confirmado",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};

function fmtAge(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h >= 1) {
    // 1h 30m (con minutos si no son cero)
    const mm = total % 3600 >= 60 ? ` ${m}m` : "";
    return `${h}h${mm}`;
  }
  if (m >= 1) return `${m}min`;
  return "ahora";
}

/**
 * Extrae los eventos de `status_history` (si existen) y añade el
 * `created_at` como evento "pending" si falta. Devuelve una lista de
 * `{status, at, label, ageMs, ageLabel}`. `now` es un parámetro para
 * permitir que el caller dispare re-render periódico sin que este
 * helper tenga estado propio.
 */
export function computeStageEvents(order, now = Date.now()) {
  if (!order) return [];
  const history = Array.isArray(order.status_history) ? order.status_history : [];
  // Merge: si history trae los eventos claves, confiamos en él;
  // si no, reconstruimos con created_at + status actual.
  const events = [];
  const seen = new Set();
  for (const ev of history) {
    if (!ev?.at || !ev?.status) continue;
    if (seen.has(ev.status)) continue; // primer timestamp de cada status
    seen.add(ev.status);
    events.push({ status: ev.status, at: ev.at });
  }
  // Si ni siquiera hay "pending", usamos created_at como fallback.
  if (!seen.has("pending") && order.created_at) {
    events.unshift({ status: "pending", at: order.created_at });
  }
  // Ordenar cronológicamente.
  events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  return events.map((ev) => {
    const ts = new Date(ev.at).getTime();
    const ageMs = Math.max(0, now - ts);
    return {
      status: ev.status,
      at: ev.at,
      label: STATUS_LABEL[ev.status] || ev.status,
      ageMs,
      ageLabel: fmtAge(ageMs),
    };
  });
}

/**
 * Duración total desde `created_at` hasta:
 *   - el timestamp de `delivered` o `cancelled` si existen (orden cerrada)
 *   - `now` en otro caso (orden abierta — reloj corre)
 */
export function totalElapsedMs(order, now = Date.now()) {
  if (!order?.created_at) return null;
  const start = new Date(order.created_at).getTime();
  const history = Array.isArray(order.status_history) ? order.status_history : [];
  const closer = history.find((ev) => ev.status === "delivered" || ev.status === "cancelled");
  const end = closer?.at ? new Date(closer.at).getTime() : now;
  return Math.max(0, end - start);
}

/** Format ms → HH:MM:SS */
export function fmtElapsedClock(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Tono según minutos totales (alineado con componentes existentes) */
export function elapsedTone(ms, { closed = false } = {}) {
  if (ms == null) return "text-neutral-400";
  if (closed) return "text-neutral-500"; // ya cerrado, neutro
  const min = ms / 60000;
  if (min >= 30) return "text-red-600 font-bold";
  if (min >= 15) return "text-amber-600";
  return "text-neutral-700";
}
